import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Landmark, Factory, Building2, Crown, Shield, Sparkles, Castle,
  BookOpen, Home, Zap, ArrowLeft, ExternalLink, PlayCircle, Upload, Loader2,
} from "lucide-react";

import { SiteFooter, FinalCTA } from "@/components/site/FinalCTA";
import AuthDialog from "@/components/site/AuthDialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FAMOUS_FAMILIES } from "@/data/famousFamilies";
import { toast } from "sonner";

const ICONS = {
  Landmark, Factory, Building2, Crown, Shield, Sparkles, Castle,
  BookOpen, Home, Zap,
};

export default function FamilyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const base = FAMOUS_FAMILIES.find((f) => f.id === id);
  const Icon = base ? (ICONS[base.icon] || Landmark) : Landmark;

  useEffect(() => {
    setError(null); setDetail(null);
    api.get(`/famous-families/${id}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [id]);

  if (!base) {
    return (
      <div className="min-h-screen bg-ivory pt-40 px-6">
        <p className="text-ink-50">Family not found.</p>
        <Link to="/famous-families" className="text-tulsi ink-link">← Back to Famous Families</Link>
      </div>
    );
  }

  return (
    <div data-testid={`family-detail-${id}`} className="min-h-screen bg-ivory text-ink">
      {/* Hero */}
      <section className="relative pt-40 pb-16 border-b border-ivory-300 overflow-hidden">
        <img
          aria-hidden
          src={base.portrait}
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.14] mix-blend-multiply pointer-events-none"
          alt=""
        />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
          <button
            onClick={() => navigate("/famous-families")}
            className="inline-flex items-center gap-2 text-sm text-ink-50 hover:text-ink ink-link mb-8"
          >
            <ArrowLeft size={14} /> All famous families
          </button>

          <div className="flex items-center gap-4">
            <div className="font-serif text-2xl text-saffron">{base.number}</div>
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full text-ivory"
              style={{ backgroundColor: base.accent }}
            >
              <Icon size={18} strokeWidth={1.6} />
            </span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-ink-50">
              {base.type} · {base.community}
            </span>
          </div>

          <h1 className="mt-6 font-serif tracking-tighter leading-[0.95] text-ink text-[3rem] sm:text-[4rem] md:text-[5rem] lg:text-[6.2rem]">
            {base.name}
          </h1>

          <p className="mt-6 max-w-3xl text-ink-50 text-lg md:text-xl font-light leading-relaxed">
            {base.legacy}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={base.wiki}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-5 py-2.5 text-sm hover:border-ink hover:bg-ink hover:text-ivory transition"
            >
              <ExternalLink size={14} /> Wikipedia
            </a>
            <a
              href={base.video}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-saffron text-ivory px-5 py-2.5 text-sm hover:bg-saffron-deep transition"
            >
              <PlayCircle size={16} /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* Facts strip */}
      <section className="relative py-10 border-b border-ivory-300">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Origin</div>
            <div className="mt-2 font-serif text-xl text-ink">{base.origin}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Known for</div>
            <div className="mt-2 font-serif text-xl text-ink">{base.known_for}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Type</div>
            <div className="mt-2 font-serif text-xl text-ink">{base.type}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Community</div>
            <div className="mt-2 font-serif text-xl text-ink">{base.community}</div>
          </div>
        </div>
      </section>

      {/* Photo gallery */}
      {detail?.gallery && (
        <section className="relative py-20">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Photo gallery</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink">
              Places, portraits and paper.
            </h2>
            <div className="mt-8 grid grid-cols-12 gap-6">
              {detail.gallery.map((g, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.06 }}
                  className={`col-span-12 ${i === 0 ? "md:col-span-8" : "md:col-span-4"} group`}
                >
                  <div className="overflow-hidden rounded-2xl border border-ivory-300">
                    <img
                      src={g.url}
                      alt={g.caption}
                      referrerPolicy="no-referrer"
                      className={`w-full ${i === 0 ? "h-[420px]" : "h-[280px]"} object-cover grayscale-[0.1] sepia-[0.08] group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out`}
                    />
                  </div>
                  <figcaption className="mt-2 font-serif italic text-ink-50 text-sm">
                    {g.caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lineage + Timeline */}
      <section className="relative py-20 bg-ivory-100/60 border-y border-ivory-300">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Family lineage</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              Names carried across generations.
            </h2>
            <ol className="mt-8 space-y-3">
              {base.lineage.map((name, i) => (
                <li key={name} className="flex items-center gap-4">
                  <span className="font-mono text-[10px] tracking-[0.24em] text-ink-50 w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif italic text-xl md:text-2xl text-ink">{name}</span>
                </li>
              ))}
            </ol>
            {(base.patriarchs.length > 0 || base.matriarchs.length > 0) && (
              <div className="mt-8 grid grid-cols-2 gap-6">
                {base.patriarchs.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Patriarchs</div>
                    <ul className="mt-2 space-y-1 text-ink">
                      {base.patriarchs.map((p) => <li key={p}>· {p}</li>)}
                    </ul>
                  </div>
                )}
                {base.matriarchs.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Matriarchs</div>
                    <ul className="mt-2 space-y-1 text-ink">
                      {base.matriarchs.map((p) => <li key={p}>· {p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-16">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Timeline</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              Major events by generation.
            </h2>
            {detail?.timeline ? (
              <ol className="mt-8 relative border-l border-ivory-300 pl-8 space-y-6">
                {detail.timeline.map((t, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.03 }}
                    className="relative"
                  >
                    <span className="absolute -left-[41px] top-1.5 w-3 h-3 rounded-full bg-saffron" />
                    <div className="font-serif text-2xl text-ink leading-none">{t.year}</div>
                    <div className="mt-2 text-ink-50 leading-relaxed">{t.event}</div>
                  </motion.li>
                ))}
              </ol>
            ) : (
              <div className="mt-6 space-y-3">
                {[1, 2, 3, 4].map((i) => <div key={i} className="h-4 rounded bg-ivory-200 animate-pulse" style={{ width: `${60 + (i % 3) * 15}%` }} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Heritage sites + notable members */}
      <section className="relative py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Heritage sites</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              Places associated with the family.
            </h2>
            {detail?.heritage_sites?.length ? (
              <ul className="mt-8 space-y-3">
                {detail.heritage_sites.map((s) => (
                  <li key={s} className="border-b border-ivory-300 pb-3">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="font-serif text-xl text-ink">{s}</span>
                      <span className="text-[10px] uppercase tracking-[0.24em] text-ink-50">Heritage site</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-10">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Notable members</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              Biographies of prominent descendants.
            </h2>
            {detail?.notable_members?.length ? (
              <ul className="mt-8 space-y-4">
                {detail.notable_members.map((m) => (
                  <li key={m.name} className="border-b border-ivory-300 pb-4">
                    <div className="font-serif text-xl text-ink">{m.name}</div>
                    <div className="mt-1 text-sm text-ink-50 italic">{m.role}</div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </section>

      {/* Community gallery */}
      <CommunityGallery familyId={id} familyName={base.name} />

      {/* Sources */}
      <section className="relative py-16 bg-ink text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.3em] text-marigold">Sources</span>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight leading-tight">
            Where this profile draws from.
          </h2>
          {detail?.sources?.length ? (
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10">
              {detail.sources.map((s) => (
                <li key={s.url} className="border-b border-ivory/10 py-4">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-baseline justify-between gap-4 group"
                  >
                    <span className="font-serif text-lg group-hover:text-marigold transition">
                      {s.label}
                    </span>
                    <ExternalLink size={14} className="text-ivory/40 group-hover:text-marigold transition" />
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-8 text-xs text-ivory/50">
            Content curated by IndianFamilyTree.com for editorial purposes. Please refer to the linked
            sources for authoritative historical references.
          </p>
        </div>
      </section>

      {error && (
        <section className="py-8">
          <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-sm text-rust">{error}</div>
        </section>
      )}

      {/* Prev / next among the 10 */}
      <FamilyNextPrev id={id} />

      <FinalCTA onStart={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
      <SiteFooter />
    </div>
  );
}


function FamilyNextPrev({ id }) {
  const idx = FAMOUS_FAMILIES.findIndex((f) => f.id === id);
  if (idx < 0) return null;
  const prev = idx > 0 ? FAMOUS_FAMILIES[idx - 1] : null;
  const next = idx < FAMOUS_FAMILIES.length - 1 ? FAMOUS_FAMILIES[idx + 1] : null;
  return (
    <section className="relative py-10 border-t border-ivory-300">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-2 gap-6">
        <div>
          {prev && (
            <Link to={`/famous-families/${prev.id}`} className="group block border border-ivory-300 rounded-2xl p-6 hover:border-ink transition">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">← Previous</div>
              <div className="mt-2 font-serif text-2xl text-ink group-hover:text-saffron transition leading-tight">
                {prev.name}
              </div>
            </Link>
          )}
        </div>
        <div className="text-right">
          {next && (
            <Link to={`/famous-families/${next.id}`} className="group block border border-ivory-300 rounded-2xl p-6 hover:border-ink transition">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Next →</div>
              <div className="mt-2 font-serif text-2xl text-ink group-hover:text-saffron transition leading-tight">
                {next.name}
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}


function CommunityGallery({ familyId, familyName }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [file, setFile] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const fileRef = useRef(null);

  async function refresh() {
    try {
      const { data } = await api.get(`/famous-families/${familyId}/submissions`);
      setItems(data?.submissions || []);
    } catch { /* noop */ }
  }

  useEffect(() => { refresh(); }, [familyId]);

  async function submit(e) {
    e.preventDefault();
    if (!user) { setAuthOpen(true); return; }
    if (!file) { toast.error("Please choose a photo to upload."); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Please keep uploads under 6 MB."); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    form.append("credit", credit);
    try {
      await api.post(`/famous-families/${familyId}/submissions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Thank you — your photograph is now in the gallery.");
      setCaption(""); setCredit(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  }

  return (
    <section
      data-testid={`community-gallery-${familyId}`}
      className="relative py-20 bg-ivory-100/60 border-y border-ivory-300"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron">Community gallery</span>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
            Family photographs
            <span className="italic text-tulsi"> from you.</span>
          </h2>
          <p className="mt-5 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-md">
            Do you hold a photograph tied to {familyName}? An ancestral home, a wedding, a portrait,
            a document? Add it below — signed-in visitors can contribute images that become part of
            this family's living archive.
          </p>

          <form onSubmit={submit} className="mt-8 border border-ivory-300 rounded-2xl p-6 bg-ivory">
            <label
              htmlFor={`upload-${familyId}`}
              data-testid={`community-upload-drop-${familyId}`}
              className={`block border-2 border-dashed rounded-xl px-4 py-8 text-center cursor-pointer transition ${
                file ? "border-tulsi bg-tulsi/5" : "border-ivory-300 hover:border-ink"
              }`}
            >
              <input
                ref={fileRef}
                id={`upload-${familyId}`}
                data-testid={`community-upload-input-${familyId}`}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-2 text-ink-50">
                <Upload size={20} />
                <span className="font-serif text-lg text-ink">
                  {file ? file.name : "Choose a photo"}
                </span>
                <span className="text-xs">JPEG, PNG or WebP · up to 6 MB</span>
              </div>
            </label>

            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Caption</label>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={280}
                placeholder="Where and when was this taken?"
                data-testid={`community-upload-caption-${familyId}`}
                className="mt-2 w-full h-11 bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none font-serif text-lg"
              />
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Credit (optional)</label>
              <input
                value={credit}
                onChange={(e) => setCredit(e.target.value)}
                maxLength={120}
                placeholder="Photographer or family holder"
                data-testid={`community-upload-credit-${familyId}`}
                className="mt-2 w-full h-11 bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none font-serif text-lg"
              />
            </div>

            <button
              type="submit"
              disabled={uploading}
              data-testid={`community-upload-submit-${familyId}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-saffron text-ivory px-6 py-3 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : user ? "Contribute to the gallery" : "Sign in to contribute"}
            </button>
            <p className="mt-3 text-xs text-ink-50">
              By contributing you confirm you have the right to share this image. Submissions are
              auto-approved for MVP and may be curated later.
            </p>
          </form>
        </div>

        <div className="col-span-12 md:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">
            {items.length} community photograph{items.length === 1 ? "" : "s"}
          </div>
          {items.length === 0 ? (
            <div className="mt-6 border border-dashed border-ivory-300 rounded-2xl p-12 text-center">
              <div className="font-serif text-2xl text-ink">Be the first to contribute.</div>
              <p className="mt-2 text-ink-50">
                No community photographs yet — your submission will start this gallery.
              </p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {items.map((s) => (
                <motion.figure
                  key={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  data-testid={`community-photo-${s.id}`}
                  className="border border-ivory-300 rounded-2xl overflow-hidden bg-ivory"
                >
                  <img
                    src={s.url}
                    alt={s.caption || "Family photograph"}
                    className="w-full h-64 object-cover"
                  />
                  <figcaption className="p-4">
                    {s.caption && (
                      <p className="font-serif text-ink text-lg leading-tight">{s.caption}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-3 text-xs text-ink-50">
                      <span>
                        Shared by <span className="text-ink">{s.user_name || "a visitor"}</span>
                      </span>
                      {s.credit && <span className="italic">Credit: {s.credit}</span>}
                    </div>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          )}
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab="login" />
    </section>
  );
}
